import { api } from "./api.js";

// b64url <-> ArrayBuffer for the WebAuthn wire format
function toBuf(b64u) {
  const s = b64u.replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4 ? 4 - (s.length % 4) : 0;
  const bin = atob(s + "=".repeat(pad));
  const a = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
  return a.buffer;
}
function toB64u(buf) {
  const a = new Uint8Array(buf);
  let bin = "";
  for (const b of a) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function platformAuthAvailable() {
  try {
    return (
      !!window.PublicKeyCredential &&
      (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
    );
  } catch {
    return false;
  }
}

function deviceLabel() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android";
  return "Device";
}

// Passkeys live on a specific device/browser — the server only knows the user
// enrolled SOMEWHERE. Track per-device (per-origin localStorage) which users
// hold a passkey HERE, so we only auto-trigger Face ID where it can succeed
// and keep offering enrollment on devices that don't have one yet.
export function hasDevicePasskey(userId) {
  try {
    return localStorage.getItem(`bt_passkey_${userId}`) === "1";
  } catch {
    return false;
  }
}
function markDevicePasskey(userId) {
  try {
    localStorage.setItem(`bt_passkey_${userId}`, "1");
  } catch {
    /* private mode — harmless */
  }
}

// Enroll Face ID / Touch ID for the CURRENTLY logged-in user on THIS device.
export async function enrollBiometric(userId) {
  const { publicKey } = await api("/api/auth/webauthn/register-options", { method: "POST", body: {} });
  const options = {
    ...publicKey,
    challenge: toBuf(publicKey.challenge),
    user: { ...publicKey.user, id: toBuf(publicKey.user.id) },
    excludeCredentials: (publicKey.excludeCredentials || []).map((c) => ({ ...c, id: toBuf(c.id) })),
  };

  let cred;
  try {
    cred = await navigator.credentials.create({ publicKey: options });
  } catch (e) {
    // SOP §7: InvalidStateError = this device already holds a passkey for this
    // app — the user is set up. Treat as success, not an error.
    if (e.name === "InvalidStateError") {
      if (userId) markDevicePasskey(userId);
      return { ok: true, alreadyEnrolled: true };
    }
    throw e;
  }

  await api("/api/auth/webauthn/register-verify", {
    method: "POST",
    body: {
      credentialId: toB64u(cred.rawId),
      clientDataJSON: toB64u(cred.response.clientDataJSON),
      attestationObject: toB64u(cred.response.attestationObject),
      deviceLabel: deviceLabel(),
    },
  });
  if (userId) markDevicePasskey(userId);
  return { ok: true };
}

// Sign in with Face ID / Touch ID for the chosen user id.
export async function biometricLogin(userId) {
  const { publicKey } = await api("/api/auth/webauthn/login-options", {
    method: "POST",
    body: { userId },
  });
  const options = {
    ...publicKey,
    challenge: toBuf(publicKey.challenge),
    allowCredentials: (publicKey.allowCredentials || []).map((c) => ({ ...c, id: toBuf(c.id) })),
  };
  const assertion = await navigator.credentials.get({ publicKey: options });
  await api("/api/auth/webauthn/login-verify", {
    method: "POST",
    body: {
      userId,
      credentialId: toB64u(assertion.rawId),
      clientDataJSON: toB64u(assertion.response.clientDataJSON),
      authenticatorData: toB64u(assertion.response.authenticatorData),
      signature: toB64u(assertion.response.signature),
    },
  });
  markDevicePasskey(userId); // this device just proved it holds a working passkey
  return { ok: true };
}
